import type { CoinLedgerItem } from '@/api'
import { Badge, Empty } from './ui'

/**
 * 额度账本表格 —— **全站唯一一份实现**。
 *
 * 两个地方要展示同一张表：账号中心（`/account`，看自己的）与后台管理的
 * 「账本」弹层（看任意账号的）。两处各写一份的话，列顺序、正负号颜色、
 * 「结算」这个中文标签都会各自漂移 —— 用户会以为两边口径不一样。
 *
 * `maxHeight` 只在弹层里传（弹层必须限高，否则内容把整屏顶穿）；
 * 账号中心是**整页**，表格跟着页面滚就行，传了反而多出一条内滚动条。
 */
export function LedgerTable({ items, maxHeight }: { items: CoinLedgerItem[]; maxHeight?: string }) {
  if (!items.length) {
    return <Empty icon="coin" title="还没有任何额度变动" text="第一次跑任务后，这里会出现扣费记录。" />
  }
  return (
    <div className="table__wrap" style={maxHeight ? { maxHeight, overflowY: 'auto' } : undefined}>
      <table className="table">
        <thead>
          <tr>
            <th>时间</th>
            <th>类型</th>
            <th>变动</th>
            <th>余额</th>
            <th>说明</th>
          </tr>
        </thead>
        <tbody>
          {items.map((i) => (
            <tr key={i.id}>
              <td className="text-xs muted">{new Date(i.createdAt).toLocaleString()}</td>
              <td>
                <Badge tone={KIND[i.kind]?.tone ?? 'neutral'}>{KIND[i.kind]?.label ?? i.kind}</Badge>
              </td>
              <td className="mono" style={{ color: i.coins >= 0 ? 'hsl(var(--success))' : undefined }}>
                {i.coins >= 0 ? '+' : ''}
                {i.coins.toFixed(2)}
              </td>
              <td className="mono">{i.balanceAfter.toFixed(2)}</td>
              <td className="text-xs">{i.note || i.ref}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/**
 * 账本里的四种 `kind`。**中文标签只有这一份真源** —— 后端存的是英文枚举，
 * 界面上的说法（尤其是 `adjust`）换个地方写就可能变成「调整」，
 * 而它在业务上其实是「任务收尾结算」。
 */
const KIND: Record<CoinLedgerItem['kind'], { label: string; tone: 'success' | 'danger' | 'info' | 'warning' }> = {
  grant: { label: '入账', tone: 'success' },
  spend: { label: '扣费', tone: 'danger' },
  refund: { label: '退款', tone: 'info' },
  adjust: { label: '结算', tone: 'warning' },
}
